import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import AdminNavbar from '../components/AdminNavbar';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  collectionGroup,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

function Analytics() {
  const { t } = useTranslation();
  const [counts, setCounts] = useState({
    students: 0,
    teachers: 0,
    groups: 0,
    admins: 0,
  });

  const [enrolmentData, setEnrolmentData] = useState([
    { name: 'Sep', enrolments: 0 },
    { name: 'Oct', enrolments: 0 },
    { name: 'Nov', enrolments: 0 },
    { name: 'Dec', enrolments: 0 },
    { name: 'Jan', enrolments: 0 },
    { name: 'Feb', enrolments: 0 },
    { name: 'Mar', enrolments: 0 },
    { name: 'Apr', enrolments: 0 },
    { name: 'May', enrolments: 0 },
    { name: 'Jun', enrolments: 0 },
  ]);

  const [teacherData, setTeacherData] = useState([]);

  // Vérification des droits d'administrateur
  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        const user = auth.currentUser;

        // Si aucun utilisateur n'est connecté, ne rien faire
        if (!user) return;

        // Requête pour vérifier si l'utilisateur est un admin
        const adminQuery = query(
          collection(db, 'admin'),
          where('uid', '==', user.uid)
        );

        const querySnapshot = await getDocs(adminQuery);

        if (querySnapshot.empty) {
          console.warn('User is not an admin. Signing out...');
          await signOut(auth);
        }
      } catch (err) {
        console.error('Error verifying admin access:', err);
        await signOut(auth); // Déconnexion par sécurité
      }
    };

    verifyAdmin();
  }, []);

  // Récupération des comptes et des inscriptions
  useEffect(() => {
    const fetchCountsAndEnrolments = async () => {
      try {
        // Récupération des comptes
        const [studentsSnapshot, teachersSnapshot, groupsSnapshot, adminsSnapshot] =
          await Promise.all([
            getDocs(collection(db, 'students')),
            getDocs(collection(db, 'teachers')),
            getDocs(collection(db, 'groups')),
            getDocs(collection(db, 'admin')),
          ]);

        setCounts({
          students: studentsSnapshot.size,
          teachers: teachersSnapshot.size,
          groups: groupsSnapshot.size,
          admins: adminsSnapshot.size,
        });

        // Traitement des inscriptions pour le graphique
        const enrolmentsByMonth = {
          Sep: 0,
          Oct: 0,
          Nov: 0,
          Dec: 0,
          Jan: 0,
          Feb: 0,
          Mar: 0,
          Apr: 0,
          May: 0,
          Jun: 0,
        };

        studentsSnapshot.docs.forEach((doc) => {
          const { enrollments } = doc.data();
          if (Array.isArray(enrollments)) {
            enrollments.forEach((enrolment) => {
              const entryDate = new Date(enrolment.entryDate);
              const month = entryDate.toLocaleString('default', {
                month: 'short',
              });
              if (enrolmentsByMonth[month] !== undefined) {
                enrolmentsByMonth[month]++;
              }
            });
          }
        });

        // Mise à jour de l'état avec les inscriptions calculées
        const updatedData = enrolmentData.map((month) => ({
          name: month.name,
          enrolments: enrolmentsByMonth[month.name] || 0,
        }));
        setEnrolmentData(updatedData);
      } catch (error) {
        console.error('Error fetching counts or enrolments:', error);
      }
    };

    fetchCountsAndEnrolments();
  }, [enrolmentData]);

  // Récupération des données analytiques, y compris le total des fichiers
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        // Récupération des comptes
        const [studentsSnapshot, teachersSnapshot, groupsSnapshot, adminsSnapshot] =
          await Promise.all([
            getDocs(collection(db, 'students')),
            getDocs(collection(db, 'teachers')),
            getDocs(collection(db, 'groups')),
            getDocs(collection(db, 'admin')),
          ]);

        setCounts({
          students: studentsSnapshot.size,
          teachers: teachersSnapshot.size,
          groups: groupsSnapshot.size,
          admins: adminsSnapshot.size,
        });

        // Récupération des enseignants
        const teachers = teachersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Récupération de tous les fichiers en une seule requête
        const filesQuery = query(collectionGroup(db, 'files'));
        const filesSnapshot = await getDocs(filesQuery);

        // Création d'un mapping groupeId -> nombre de fichiers
        const groupFilesCount = {};
        filesSnapshot.docs.forEach((fileDoc) => {
          const filePath = fileDoc.ref.path; // ex: "groups/{groupId}/files/{fileId}"
          const pathSegments = filePath.split('/');
          const groupId = pathSegments[1]; // "groups/{groupId}/files/{fileId}"
          if (groupFilesCount[groupId]) {
            groupFilesCount[groupId]++;
          } else {
            groupFilesCount[groupId] = 1;
          }
        });

        // Fonction pour calculer le total des étudiants pour un enseignant
        const calculateTotalStudentsForTeacher = async (groupIds) => {
          try {
            let totalStudents = 0;
            for (const groupId of groupIds) {
              const groupDoc = await getDoc(doc(db, 'groups', groupId));
              if (groupDoc.exists()) {
                const { studentIds } = groupDoc.data();
                totalStudents += Array.isArray(studentIds) ? studentIds.length : 0;
              }
            }
            return totalStudents;
          } catch (error) {
            console.error('Error calculating total students:', error);
            return 0;
          }
        };

        // Calcul des données des enseignants avec étudiants et fichiers
        const teacherDataWithDetails = await Promise.all(
          teachers.map(async (teacher) => {
            const totalStudents = await calculateTotalStudentsForTeacher(
              teacher.groupIds || []
            );

            // Calcul du total des fichiers en utilisant le mapping groupId -> fichiers
            const totalFiles = (teacher.groupIds || []).reduce((acc, groupId) => {
              return acc + (groupFilesCount[groupId] || 0);
            }, 0);

            return {
              name: `${teacher.firstName} ${teacher.lastName}`,
              totalStudents,
              totalFiles, // Ajout du total des fichiers
            };
          })
        );

        setTeacherData(teacherDataWithDetails);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchAnalyticsData();
  }, []);

  const tickets = [
    { label: t('الطلاب'), value: counts.students },
    { label: t('الأساتذة'), value: counts.teachers },
    { label: t('المجموعات (الأقسام)'), value: counts.groups },
    { label: t('طاقم الإدارة'), value: counts.admins },
  ];

  return (
    <Box style={{ backgroundColor: '#f5f7fb', minHeight: '100vh' }}>
      <div style={{ backgroundColor: '#f2f4f4' }}>
        <AdminNavbar />

        {/* Section Supérieure */}
        <div
          style={{
            backgroundColor: '#12344d',
            height: '200px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              fontSize: '35px',
              fontFamily: 'Times New Roman, serif',
              color: 'white',
              textAlign: 'center',
            }}
          >
            {t('صفحة التحليلات وتتبع تقدم المدرسة')}
          </div>
        </div>

        {/* Cartes des Tickets */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'center',
            alignItems: 'center',
            gap: '50px',
            marginTop: '50px',
          }}
        >
          {tickets.map((ticket, index) => (
            <Card
              key={index}
              sx={{
                width: '250px',
                padding: '10px',
                textAlign: 'center',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
              }}
            >
              <CardContent>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 'bold', marginTop: '10px' }}
                >
                  {ticket.value}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#555', marginTop: '5px', fontSize: '20px' }}
                >
                  {ticket.label}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Graphiques et Tableaux */}
        <Card
          sx={{
            margin: '50px auto',
            padding: '20px',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            width: '90%',
          }}
        >
          <Typography
            variant="h6"
            sx={{ marginBottom: '20px', textAlign: 'center', fontSize: '25px' }}
          >
            <b>{t('التسجيلات على مدار الأشهر')}</b>
          </Typography>

          {/* Graphique Linéaire */}
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={enrolmentData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="enrolments"
                stroke="#8884d8"
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>

          {/* Tableau d'Activité */}
          <TableContainer component={Paper} sx={{ marginTop: '20px' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '18px' }}>{t('الأستاذ')}</TableCell>
                  <TableCell sx={{ fontSize: '18px' }}>
                    {t('إجمالي التسجيلات')}
                  </TableCell>
                  <TableCell sx={{ fontSize: '18px' }}>
                    {t('إجمالي الملفات المرفوعة')}
                  </TableCell>
                  <TableCell sx={{ fontSize: '18px' }}>
                    {t('متوسط وقت الاستجابة')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teacherData.map((teacher, index) => (
                  <TableRow key={index}>
                    <TableCell>{teacher.name}</TableCell>
                    <TableCell>{teacher.totalStudents}</TableCell>
                    <TableCell>{teacher.totalFiles}</TableCell> {/* Affichage des fichiers */}
                    <TableCell>{t("0h00m")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <div>
          <br />
        </div>
      </div>
    </Box>
  );
}

export default Analytics;
